import { Box, Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthProvider, useNotify } from 'react-admin';
import { Identity } from '../../../interfaces/auth';
import { RpmCalculationFilters } from '../../../interfaces/billing';
import { BillingClaimPreview, BillingPagination } from '../../../interfaces/billing.rpm';
import { IClinic } from '../../../interfaces/clinic';
import { SelectOption } from '../../../interfaces/common';
import { PayerItem } from '../../../interfaces/eligibility';
import { BillingService } from '../../../services';
import coreService from '../../../services/core';
import eligibilityService from '../../../services/eligibility';
import { formatDateStringToYYYYMMDD } from '../../../util/dateFormat';
import { INVALID_STORAGE_ERROR } from '../common/constants';
import { RPMFilters } from '../common/RPMFilters';
import { ActionableTab } from '../common/types';
import { isClaimCreatedSuccessfully } from '../helpers';
import {
  FilterConfiguration,
  Filters,
  INITIAL_PAGINATION,
  LoadingStates,
  RPMCptCodes,
} from '../types/rpms.types';
import { DiscardCalculationModal } from './DiscardCalculationModal';
import { ProcessClaimsModal } from './ProcessClaimsModal';
import { RPMCalculationTable } from './RPMCalculationTable';

type RPMCalculationProps = ActionableTab & {
  processId?: number;
  clinics: IClinic[];
};

const filterConfigurations = [
  {
    key: 'clinic',
    label: 'Clinic',
    options: [],
    section: 'Patient Selection',
    type: 'select',
  },
  {
    key: 'insuranceCompany',
    label: 'Insurance Company',
    options: [],
    section: 'Patient Selection',
    type: 'autocomplete',
    fetchOptions: async (searchTerm: string) => {
      try {
        const response = await eligibilityService.getPayers({
          filter: searchTerm || null,
        });
        const options =
          response?.data?.map((payer: PayerItem) => ({
            label: payer.name,
            value: payer.id,
          })) || [];
        return options;
      } catch (error) {
        console.error('Error fetching insurance companies:', error);
        return [];
      }
    },
  },
  {
    key: 'dateOfService',
    label: 'Date Of Service',
    type: 'date',
  },
  {
    key: 'cptCodes',
    label: 'CPT Codes',
    options: Object.values(RPMCptCodes).map((value) => ({ value, label: value })),
    section: 'Patient Selection',
    type: 'select',
  },
  {
    key: 'memberSearch',
    label: 'Member Search',
    options: [],
    section: 'Patient Selection',
    type: 'autocomplete',
    fetchOptions: async (searchTerm: string) => {
      try {
        if (!searchTerm) {
          return [];
        }

        const response = await coreService.fetchMembers({
          searchTerm,
        });
        const options =
          response?.data?.map((member) => ({
            label: member.fullName,
            value: member.uuid,
          })) || [];
        return options;
      } catch (error) {
        console.error('Error fetching members:', error);
        return [];
      }
    },
  },
  {
    key: 'claimStatus',
    label: 'Claim Status',
    options: [
      { value: 'VALID', label: 'Valid' },
      { value: 'INVALID', label: 'Invalid' },
    ],
    section: 'Claim Status',
    type: 'select',
  },
] as FilterConfiguration<Filters>[];

const initialFilters: Filters = {
  clinic: [],
  insuranceCompany: [],
  dateOfService: [],
  cptCodes: [],
  memberSearch: [],
  claimStatus: [],
};

export const RPMCalculation: React.FC<RPMCalculationProps> = ({
  onAfterAction,
  processId,
  clinics,
}) => {
  const [claimsData, setClaimsData] = useState<BillingClaimPreview[]>([]);
  const [pagination, setPagination] = useState<BillingPagination>(INITIAL_PAGINATION);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    isApplying: false,
    isClearing: false,
    isProcessing: false,
    isDiscarding: false,
  });
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);
  const [skipEffect, setSkipEffect] = useState(false);
  const [allRecordsSelected, setAllRecordsSelected] = useState(false);
  const [excludedRows, setExcludedRows] = useState<{ id: number; index: number }[]>([]);

  const authProvider = useAuthProvider();
  const notify = useNotify();

  const clinicOptions: SelectOption[] = useMemo(
    () => clinics.map((clinic) => ({ label: clinic.name, value: `${clinic.id}` })),
    [clinics],
  );

  const buildRpmParameters = useCallback(
    (
      currentFilters: Filters,
      page: number,
      limit: number,
      options: {
        includeDate?: boolean;
        includeFilters?: boolean;
      } = {
        includeDate: true,
        includeFilters: true,
      },
    ): RpmCalculationFilters => {
      const parameters: RpmCalculationFilters = {
        page,
        limit,
        clinicIds: [],
        insuranceCompanyIds: [],
        cptCodes: [],
        memberUuids: [],
        claimStatus: [],
        dateOfService: undefined,
        processId,
      };

      if (options.includeFilters) {
        parameters.clinicIds = currentFilters.clinic.map((clinic) => parseInt(clinic, 10));
        parameters.insuranceCompanyIds = currentFilters.insuranceCompany || [];
        parameters.cptCodes = currentFilters.cptCodes;
        parameters.memberUuids = currentFilters.memberSearch || [];
        parameters.claimStatus = currentFilters.claimStatus || [];
      }

      if (options.includeDate && currentFilters.dateOfService) {
        parameters.dateOfService = currentFilters.dateOfService?.[0]
          ? formatDateStringToYYYYMMDD(currentFilters.dateOfService?.[0])
          : '';
      }

      return parameters;
    },
    [processId],
  );

  const applyClaimStatusFilter = (data: BillingClaimPreview[], statusFilters: string[]) => {
    if (!statusFilters.length) return data;

    return data.filter((claim) => {
      const displayStatus = isClaimCreatedSuccessfully(claim.status) ? 'VALID' : 'INVALID';
      return statusFilters.includes(displayStatus);
    });
  };

  const fetchClaims = useCallback(async (parameters: RpmCalculationFilters) => {
    setLoadingStates((prev) => ({ ...prev, isApplying: true }));

    try {
      // Extract claimStatus for client-side filtering
      const { claimStatus, ...serverParameters } = parameters;

      const response = await new BillingService().getClaimsCalculated(serverParameters);
      const rawData = response?.data || [];

      // Apply client-side filtering for claim status
      const filteredData = applyClaimStatusFilter(rawData, claimStatus || []);

      setClaimsData(filteredData);

      setPagination(response?.pagination || INITIAL_PAGINATION);
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, isApplying: false }));
    }
  }, []);

  const handlePageChange = (page: number) => {
    const parameters = buildRpmParameters(filters, page, pagination.limit);
    fetchClaims(parameters);
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    const parameters = buildRpmParameters(filters, pagination.currentPage, newLimit);
    fetchClaims(parameters);
  };

  const exportClaimsWithSelection = async (
    billingService: BillingService,
    allRecordsSelected: boolean,
    selectedRows: number[],
    filters: Filters,
    excludedRows: { id: number; index: number }[],
    claimsData: BillingClaimPreview[],
  ) => {
    const identity = (await authProvider.getIdentity!()) as Identity;

    if (!identity?.uuid || !identity?.fullName) {
      notify(INVALID_STORAGE_ERROR, 'error');
      return;
    }

    let select: number[] | null = null;
    let exclude: number[] | null = null;

    if (allRecordsSelected && excludedRows.length) {
      exclude = excludedRows.map(({ id }) => id);
    } else if (!allRecordsSelected && selectedRows.length) {
      const claimsMap = new Map(claimsData.map((claim) => [claim.id, claim]));
      select = selectedRows
        .map((index) => claimsMap.get(index)?.id)
        .filter((id): id is number => id !== undefined);
    }

    return billingService.exportClaims(
      {
        ...(select && { select }),
        ...(exclude && { exclude }),
      },
      {
        processId,
        ...(filters.clinic?.length && {
          clinicIds: filters.clinic.map((clinicId) => Number.parseInt(clinicId)),
        }),
        ...(filters.insuranceCompany?.length && { insuranceCompanyIds: filters.insuranceCompany }),
        ...(filters.dateOfService?.length && {
          dateOfService: formatDateStringToYYYYMMDD(filters.dateOfService[0] as unknown as Date),
        }),
        ...(filters.memberSearch?.length && { memberUuids: filters.memberSearch }),
        ...(filters.cptCodes?.length && { cptCodes: filters.cptCodes }),
        // Note: claimStatus is handled client-side, so we don't include it in the export parameters
      },
      { uuid: identity.uuid, fullName: identity.fullName },
      { runInBackground: true },
    );
  };

  const updatedFilterConfigurations = useMemo(
    () =>
      filterConfigurations.map((config) =>
        config.key === 'clinic' ? { ...config, options: clinicOptions } : config,
      ),
    [clinicOptions],
  );

  useEffect(() => {
    if (!skipEffect) {
      const parameters = buildRpmParameters(filters, pagination.currentPage, pagination.limit);
      fetchClaims(parameters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildRpmParameters excluded because it only depends on processId which is already included
  }, [filters, pagination.currentPage, pagination.limit, skipEffect, processId, fetchClaims]);

  const handleApplyFilters = async (appliedFilters: typeof initialFilters) => {
    setLoadingStates((prev) => ({ ...prev, isApplying: true }));
    try {
      const parameters = buildRpmParameters(appliedFilters, 1, pagination.limit);
      await fetchClaims(parameters);
      setFilters(appliedFilters);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, isApplying: false }));
    }
  };

  const handleClearFilters = async () => {
    setLoadingStates((prev) => ({ ...prev, isClearing: true }));
    setSkipEffect(true);

    try {
      await getInitialClaimsRequest();
      setFilters(initialFilters);
      setSelectedRows([]);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, isClearing: false }));
      setSkipEffect(false);
    }
  };

  const handleProcessFilters = () => {
    setIsProcessModalOpen(true);
  };

  const handleDiscardProcess = () => {
    setIsDiscardModalOpen(true);
  };

  const handleConfirmDiscard = async () => {
    const identity = (await authProvider.getIdentity!()) as Identity;

    if (!identity?.uuid || !identity?.fullName) {
      notify(INVALID_STORAGE_ERROR, 'error');
      return;
    }

    try {
      const response = await new BillingService().discardClaims(processId!, {
        fullName: identity.fullName,
        uuid: identity.uuid,
      });
      if (!response) {
        setLoadingStates((prev) => ({ ...prev, isDiscarding: true }));
        setIsDiscardModalOpen(false);
        setSelectedRows([]);
        notify(`can't discard the claims`, 'warning');

        return;
      }

      notify(`${response.message}`, 'success');

      setLoadingStates((prev) => ({ ...prev, isDiscarding: true }));
      setIsDiscardModalOpen(false);
      setSelectedRows([]);
      onAfterAction(null);
    } catch (error) {
      console.error('Error discarding calculation:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, isDiscarding: false }));
    }
  };

  const handleConfirmProcess = async () => {
    setLoadingStates((prev) => ({ ...prev, isProcessing: true }));
    try {
      const billingService = new BillingService();
      const response = await exportClaimsWithSelection(
        billingService,
        allRecordsSelected,
        selectedRows,
        filters,
        excludedRows,
        claimsData,
      );

      if (response) {
        const exportProcessId = billingService.getProcessId(response);
        onAfterAction(exportProcessId);

        // Show different notifications based on response type
        if (billingService.isBackgroundResponse(response)) {
          notify('Claims export started in the background. You will see updates in real-time.', 'info');
        } else {
          const summary = billingService.createProcessSummary(response);
          notify(`Claims export completed. ${summary}`, 'success');
        }
      }
    } catch (error) {
      console.error('Error processing claims:', error);
      notify('Error starting export job', 'error');
    } finally {
      setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
      setIsProcessModalOpen(false);
    }
  };

  const getInitialClaimsRequest = async () => {
    const parameters = buildRpmParameters(initialFilters, 1, pagination.limit, {
      includeFilters: false,
    });
    await fetchClaims(parameters);
  };

  return (
    <Box>
      <RPMFilters<Filters>
        configurations={updatedFilterConfigurations}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        onProcessFilters={handleProcessFilters}
        onProcessLabel={
          allRecordsSelected ? 'Export all selected claims' : `Export ${selectedRows.length} claims`
        }
        initialFilters={initialFilters}
        loadingStates={loadingStates}
        selectedCount={selectedRows.length}
        additionalButtons={
          <>
            <Button variant='contained' onClick={handleDiscardProcess} disabled={!processId}>
              Discard Process
            </Button>
          </>
        }
        allRecordsSelection={allRecordsSelected}
      />
      <RPMCalculationTable
        onRowSelectionChange={setSelectedRows}
        selectedRows={selectedRows}
        onRowExclusionChange={setExcludedRows}
        excludedRows={excludedRows}
        onAllRecordsSelected={setAllRecordsSelected}
        allRecordsSelected={allRecordsSelected}
        data={claimsData}
        pagination={pagination}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        isLoading={loadingStates.isApplying}
      />

      <DiscardCalculationModal
        open={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={handleConfirmDiscard}
        isLoading={loadingStates.isDiscarding}
      />

      <ProcessClaimsModal
        open={isProcessModalOpen}
        onClose={() => setIsProcessModalOpen(false)}
        onConfirm={handleConfirmProcess}
        claimsCount={selectedRows.length}
        isLoading={loadingStates.isProcessing}
        allClaimsSelected={allRecordsSelected}
      />
    </Box>
  );
};
